import { defineStore, storeToRefs } from 'pinia';
import { TimelineState } from '@/views/creatives/enums';
import { usePlayCanvasStore } from './playCanvas';
import { useCreativeStore } from './creative';

export const usePlayTimelineStore = defineStore('playTimeline', {
  state: () => ({
    timelineState: TimelineState.PEND,
    videoLoop: 0,
    // set current time to be an array but with only one content just for watch to be able to detect
    // every time when currentTime is reset.
    currentTime: [0],
    autoplayScene: false,
    FRAMES_PER_SECOND: 20,
    FRAME_MIN_TIME: (1000 / 60) * (60 / 20) - (1000 / 60) * 0.5,
    updateInPlace: false,
    zoomLevel: 60,
    horizPct: 0,
    currentVideoTime: null,
    triggerPreview: [false],
  }),

  getters: {
    getCurrentDuration(): number {
      const playCanvasStore = usePlayCanvasStore();
      return playCanvasStore.getCurrentSceneLength || 0;
    },

    getCurrentTime(state): number {
      return state.currentTime[0];
    },

    getCurrentTimePct(state): number {
      if (this.getCurrentDuration === 0) return 0;
      return state.currentTime[0] / this.getCurrentDuration;
    },
    getTimelineState(state): TimelineState {
      return state.timelineState;
    },
  },

  actions: {
    autoplaySceneIfApplicable(): void {
      const playCanvasStore = usePlayCanvasStore();
      // Don't try to autoplay if we're not in the preview.
      if (!playCanvasStore.showPreview) return;

      const creativeStore = useCreativeStore();
      const { currentCreative } = storeToRefs(creativeStore);
      const { scenes } = currentCreative.value;

      if (scenes[playCanvasStore.getCurrentSceneIndex].autoplay
        && [TimelineState.PEND, TimelineState.PAUSE].includes(this.timelineState)) {
        this.togglePlay();
      }

      // No need to check for video autoplay as playable will already be converted in the editor before reaching preview

      // // get an array of all children including group children
      // const children = [...scenes[playCanvasStore.getCurrentSceneIndex].children];
      // children.forEach((child) => {
      //   if (!child.children || child.children.length === 0) return;
      //   children.push(...child.children);
      // });

      // // filter out video layers only
      // // sort by start time
      // // select first video
      // const vid = children
      //   .filter((child) => child.type === 'Video')
      //   .sort((a, b) => a.startTime - b.startTime)
      //   .at(0);

      // // if we have a video
      // // and the video is set to autoplay
      // // and the timeline is not playing
      // // then toggle play
      // if (vid && vid.autoplay && [TimelineState.PEND, TimelineState.PAUSE].includes(this.timelineState)) this.togglePlay();
    },

    reset(): void {
      this.currentTime = [0];
      this.timelineState = TimelineState.PEND;
    },

    setTime(newTime: number): void {
      // console.log('tick', this.currentTime[0], newTime);
      this.currentTime = [Math.min(newTime, this.getCurrentDuration)];
      if (newTime >= this.getCurrentDuration) {
        // TODO: this should only set to pause if the next scene does not have an autoplaying video
        this.timelineState = TimelineState.PAUSE;
        this.currentVideoTime = null;
      }
    },

    rewindToStart():void {
      this.currentTime = [0];

      this.timelineState = TimelineState.PEND;

      // TODO: when changing scene length to be customized,
      // reconsider and maybe delete the setTimeout Function
      // setTimeout is here to insure the timelineState change
      // will trigger video to reset to beginning
      const playCanvasStore = usePlayCanvasStore();
      if (playCanvasStore.showPreview && this.getCurrentDuration !== 0) setTimeout(this.autoplaySceneIfApplicable);
    },

    pause(): void {
      this.timelineState = TimelineState.PAUSE;
    },

    // TODO: state can be deleted when custom scene duration is on and
    // onFinish now only happens in the end of the scene. This param is added
    // to solve the problem where onComplete is called twice when video finishes
    // and mess up with the timeline state if video is autoplay & video has
    // on finish to be restart the scene
    togglePlay(state?: TimelineState): void {
      if (this.currentTime[0] === this.getCurrentDuration) this.currentTime = [0];

      if (state) this.timelineState = state;
      else this.timelineState = this.timelineState === TimelineState.PLAY ? TimelineState.PAUSE : TimelineState.PLAY;

      // updateInPlace prevents the editor from creating more than one update loop which throws off scene timing
      if (this.timelineState === TimelineState.PLAY && !this.updateInPlace) {
        this.updateInPlace = true;
        this.updateTime(Date.now());
      }
    },

    resetPlay() {
      this.setTime(0);
    },

    updateTime(lastFrameTime: number): void {
      if (this.timelineState !== TimelineState.PLAY) {
        this.updateInPlace = false;
        return;
      }
      const baseTime = this.currentVideoTime === null ? this.getCurrentTime : this.currentVideoTime;
      const now = Date.now();
      const delta = (now - lastFrameTime) / 1000;
      if (delta > 0) this.setTime(baseTime + delta);
      window.requestAnimationFrame(() => this.updateTime(now));
    },
  },
});

export default usePlayTimelineStore;
